import { Box } from '@radix-ui/themes'
import { useSetAtom } from 'jotai'
import React, { type HTMLAttributes } from 'react'
import {
  DexAnchorPosition,
  type DexOverlayInstance,
} from '#app/data/models/overlay.model.js'
import { backdropClickedAtom } from '#app/data/states/overlay-state.ts'
import { cn } from '#app/utils/misc.js'

export default function Overlays() {
  return null
}

type OverlayContainerProps = HTMLAttributes<HTMLDivElement> & {
  overlay: DexOverlayInstance | undefined
  disablePointerEvents?: boolean
}

export const OverlayContainer = React.forwardRef<
  HTMLDivElement,
  OverlayContainerProps
>(
  (
    { overlay, disablePointerEvents, className, style, children, ...props },
    ref,
  ) => {
    const setBackdropClicked = useSetAtom(backdropClickedAtom)

    if (!overlay) return null

    const anchor = overlay.anchor || DexAnchorPosition.TOP_LEFT
    const anchorOffsetX = overlay.anchorOffsetX || 0
    const anchorOffsetY = overlay.anchorOffsetY || 0

    function handleBackdropClick(event: React.MouseEvent) {
      setBackdropClicked(event)
    }

    return (
      <>
        {overlay.backdropEnabled && (
          <OverlayBackdrop onClick={handleBackdropClick} />
        )}
        <Box
          ref={ref}
          className={cn('fixed', className)}
          style={{
            pointerEvents: disablePointerEvents ? 'none' : 'auto',
            overflow: 'visible',
            whiteSpace: 'nowrap',
            top: overlay.coords.y,
            left: overlay.coords.x,
            zIndex: 200,
            ...style,
          }}
          {...props}
        >
          <Box
            onClick={event => event.stopPropagation()}
            onContextMenu={event => event.stopPropagation()}
            className={cn(
              'absolute',
              anchor === DexAnchorPosition.TOP_LEFT && 'left-0 top-0',
              anchor === DexAnchorPosition.TOP &&
                'left-1/2 top-0 -translate-x-1/2 transform',
              anchor === DexAnchorPosition.TOP_RIGHT && 'right-0 top-0',
              anchor === DexAnchorPosition.RIGHT && 'right-0 -translate-y-1/2',
              anchor === DexAnchorPosition.BOTTOM_RIGHT && 'bottom-0 right-0',
              anchor === DexAnchorPosition.BOTTOM &&
                'bottom-0 left-1/2 -translate-x-1/2 transform',
              anchor === DexAnchorPosition.BOTTOM_LEFT && 'bottom-0 left-0',
              anchor === DexAnchorPosition.LEFT && 'left-0 -translate-y-1/2',
              anchor === DexAnchorPosition.CENTER &&
                'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform',
            )}
            style={{
              // width: 0,
              top: [
                DexAnchorPosition.TOP,
                DexAnchorPosition.TOP_LEFT,
                DexAnchorPosition.TOP_RIGHT,
                DexAnchorPosition.CENTER,
              ].includes(anchor)
                ? anchorOffsetY
                : undefined,
              bottom: [
                DexAnchorPosition.BOTTOM,
                DexAnchorPosition.BOTTOM_LEFT,
                DexAnchorPosition.BOTTOM_RIGHT,
              ].includes(anchor)
                ? anchorOffsetY
                : undefined,
              left: [
                DexAnchorPosition.LEFT,
                DexAnchorPosition.TOP_LEFT,
                DexAnchorPosition.BOTTOM_LEFT,
                DexAnchorPosition.CENTER,
              ].includes(anchor)
                ? anchorOffsetX
                : undefined,
              right: [
                DexAnchorPosition.RIGHT,
                DexAnchorPosition.TOP_RIGHT,
                DexAnchorPosition.BOTTOM_RIGHT,
              ].includes(anchor)
                ? anchorOffsetX
                : undefined,
            }}
          >
            {children}
          </Box>
        </Box>
      </>
    )
  },
)

OverlayContainer.displayName = 'OverlayContainer'

const OverlayBackdrop = React.forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, style, ...props }, ref) => {
  return (
    <Box
      {...props}
      className={cn(
        'pointer-events-auto fixed bottom-0 left-0 right-0 top-0 bg-black/25',
        className,
      )}
      style={{
        zIndex: 100,
        ...style,
      }}
      // Disable right clicking on backdrop
      onContextMenu={event => {
        event.preventDefault()
        event.stopPropagation()
      }}
    />
  )
})

OverlayBackdrop.displayName = 'OverlayBackdrop'
